/*
  This code defines functionalities related to managing a shopping cart on a webpage. It includes:
  - Definitions of URLs for various cart-related actions.
  - Initialization of cart data and properties.
  - Functions for interacting with the shopping cart, such as fetching, adding, updating, and removing items.
  - Functions for handling line item properties and gift box attributes.
  - Computed properties for calculating cart quantities, line items, gift box presence, and prices.
  - A cart component definition with methods and computed properties for displaying cart-related information and handling UI logic.
  - Integration with Alpine.js for registering the cart store and component.

  Note: The code is heavily commented to explain each section's purpose and functionality.
*/

const CART_GET_URL = window.Shopify.routes.root + 'cart.js';
const CART_ADD_URL = window.Shopify.routes.root + 'cart/add.js';
const CART_UPDATE_URL = window.Shopify.routes.root + 'cart/update.js';
const CART_CHANGE_URL = window.Shopify.routes.root + 'cart/change.js';

const cartDataInitial = pDefer();

const cartStore = () => ({
  cartData: null,
  giftBoxProductData: null,
  lineItemProperties: [],

  async init() {
    await this.fetchCart();
    cartDataInitial.resolve(this.cartData);
    // const productData = await fetchProductData([window.GIFT_BOX_PRODUCT_HANDLE]);
    // this.giftBoxProductData = productData[window.GIFT_BOX_PRODUCT_ID];
    this.loading = false;
  },

  async updateLineItemProperties() {
    const variantIds = this.cartData.items.map((item) => item.variant_id);
    this.lineItemProperties = await Promise.all(variantIds.map((id) => fetchLineItem(id)));
  },
  /**
   * START: FIRST-CLASS ACTIONS
   * These interact with AJAX API and must set cartData
   * consequentially.
   */
  async fetchCart() {
    const url = new URL(window.location.href);
    url.pathname = CART_GET_URL;
    url.search = new URLSearchParams({
      t: Date.now(),
    });

    const params = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const data = await request(url, params);
    this.cartData = data;
    if (this.cartData?.items?.length > 0) {
      this.updateLineItemProperties();
    }
  },

  async addToCart(body) {
    await request(CART_ADD_URL, {
      method: 'post',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    });

    this.fetchCart();
  },

  async updateCart(body) {
    const url = CART_UPDATE_URL;
    const params = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    };

    const data = await request(url, params);
    // If only remaining item is Gift Box, remove that too
    if (data.items.length === 1 && data.items[0].product_id === window.GIFT_BOX_PRODUCT_ID && !this.clearingGiftBox) {
      // note, recursive
      // should only be entered once, clearingGiftBox is saftey flag against infinite loop
      this.clearingGiftBox = true;
      await this.removeLineItem(data.items[0].key);
      await this.clearGiftBoxAttributes();
      this.clearingGiftBox = false;
    } else {
      // normal case
      this.cartData = data;
      if (this.cartData?.items?.length > 0) {
        this.updateLineItemProperties();
      }
    }
    this.clearingGiftBox = false;
  },

  async changeLineItem(body, alpineContext) {
    const url = CART_CHANGE_URL;
    const params = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    };

    const data = await request(url, params);
    this.cartData = data;
    if (this.cartData?.items?.length > 0) {
      this.updateLineItemProperties();
    }
  },

  /**
   * SECOND-CLASS ACTIONS
   * These call POST method FIRST-CLASS ACTIONS with predetermined bodies
   * They should not handle response data, but do return awaitable promises.
   */
  async removeLineItem(key) {
    return await this.changeLineItem({
      id: key,
      quantity: 0,
    });
  },

  async clearGiftBoxAttributes() {
    const cartAttributes = { ...this.cartData.attributes };
    cartAttributes['gift-wrapping'] = null;
    cartAttributes['gift-note'] = null;

    return await this.updateCart({
      attributes: cartAttributes,
    });
  },

  get normalizedCartQty() {
    return this.cartData.items
      .filter((item) => item.product_id !== window.GIFT_BOX_PRODUCT_ID)
      .reduce((acc, item) => acc + item.quantity, 0);
  },

  get lineItemsNormalized() {
    // currently normalization consists only of ensuring
    // that any gift box product is last in order

    return [...this.cartData.items].sort((a, b) =>
      a.product_id === window.GIFT_BOX_PRODUCT_ID ? 1 : b.product_id === window.GIFT_BOX_PRODUCT_ID ? -1 : 0,
    );
  },

  get giftBoxItemOnCart() {
    return this.cartData.items.find((item) => item.product_id === window.GIFT_BOX_PRODUCT_ID);
  },

  get giftBoxPrice() {
    const { giftBoxProductData } = this;
    if (giftBoxProductData) {
      return giftBoxProductData.variants[0].priceFormatted;
    }

    return '';
  },
});

const cartComponent = (freePerkThreshold, freePerkProgressMessage, freePerkMessage) => ({
  freePerkThreshold,
  freePerkProgressMessage,
  freePerkMessage,

  cartHeader() {
    return `${this.$store.cart.cartData ? ` (${this.$store.cart.normalizedCartQty})` : ''}`;
  },
  // US localization only: START
  get enableFreeShippingProgress() {
    return window.Shopify.country === 'US';
  },

  get totalForProgressCalculation() {
    // Do not include possible gift box in calculation
    return (
      this.$store.cart.cartData?.items
        .filter((item) => item.product_id !== window.GIFT_BOX_PRODUCT_ID)
        .reduce((acc, item) => acc + item.final_line_price, 0) / 100
    );
  },

  get includeFreeGift() {
    const lineItems = this.$store.cart.lineItemProperties;
    return lineItems ? lineItems.some((item) => item.hasFreeGift) : false;
  },

  get progressMessage() {
    if (this.totalForProgressCalculation < this.freePerkThreshold) {
      const remaining = this.freePerkThreshold - this.totalForProgressCalculation;

      return this.freePerkProgressMessage.replace('{amount}', `$${remaining.toFixed(2)}`);
    } else {
      return this.freePerkMessage;
    }
  },

  get progressPercentage() {
    return `${(this.totalForProgressCalculation / this.freePerkThreshold) * 100}%`;
  },
  // US localization only: END

  get cartHasDiscounts() {
    return this.$store.cart.cartData.total_discount > 0;
  },

  get discountTotal() {
    if (this.$store.cart.cartData.currency && this.$store.cart.cartData.total_discount) {
      return formatPrice(
        this.$store.cart.cartData.currency,
        this.$store.cart.cartData.total_discount ? this.$store.cart.cartData.total_discount / -100 : 0,
      );
    } else return '';
  },

  get subtotal() {
    if (this.$store.cart.cartData.currency) {
      return formatPrice(
        this.$store.cart.cartData.currency,
        this.$store.cart.cartData.total_price ? this.$store.cart.cartData.total_price / 100 : 0,
      );
    } else return '';
  },

  async init() {},
});

document.addEventListener('alpine:init', () => {
  Alpine.store('cart', cartStore());
  Alpine.data('cartComponent', cartComponent);
});
